import React, { useRef, useState } from 'react'
import styles from './FullName.module.css'
import { NextButton } from '../../../Common/CtaButton/NextButton'
import { useStepContext } from '@/components/Context/StepContext'
import { FullNameProps } from './types'
import { useAbTestGtm } from '@/components/utils/abTest'
import { FollowIcon } from '../../../FormGuidance/FollowIcon'
import { useFollowIcon } from '../../../FormGuidance/FollowIcon/useFollowIcon'
import { StepBadge } from '@/components/Common/StepBadge'

// 縦型EF step4(お名前・年齢)
export const FullNameVertical = ({ gtmTags, stepNumber }: FullNameProps) => {
    const {
        formData,
        updateFormData,
        errors,
        touchField,
        touchedFields,
        isAnimating,
        currentStep,
        handleNext,
    } = useStepContext()

    const parentRef = useRef<HTMLDivElement>(null)
    const nameFieldRef = useRef<HTMLDivElement>(null)
    const ageFieldRef = useRef<HTMLDivElement>(null)
    const nextButtonRef = useRef<HTMLDivElement>(null)
    const [isAgeFocused, setIsAgeFocused] = useState(false)

    const handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData('lastName', e.target.value)
    }

    const handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData('firstName', e.target.value)
    }

    const handleAgeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateFormData('age', e.target.value)
    }

    const handleBlur = (field: 'lastName' | 'firstName' | 'age') => {
        touchField(field)
    }

    const showLastNameError = touchedFields.lastName && errors.lastName
    const showFirstNameError = touchedFields.firstName && errors.firstName
    const showAgeError = touchedFields.age && errors.age

    const isFormValid =
        !errors.lastName &&
        !errors.firstName &&
        !errors.age &&
        formData.lastName &&
        formData.firstName &&
        formData.age &&
        formData.age.length >= 2

    const targets = [
        {
            ref: nameFieldRef,
            isComplete: isAgeFocused && !errors.lastName && !errors.firstName,
            offset: 46,
        },
        {
            ref: ageFieldRef,
            isComplete: touchedFields.age && !!formData.age && !errors.age && formData.age.length >= 2,
            offset: 46,
        },
        {
            ref: nextButtonRef,
            isComplete: !!isFormValid,
        },
    ]
    const { iconStyle } = useFollowIcon({ parentRef, targets, isAnimating, layoutKey: currentStep })

    const { dataViewLabel, dataClickLabel } = useAbTestGtm()

    return (
        <div className={styles.wrapper} ref={parentRef} data-view-label={dataViewLabel(gtmTags.dataViewLabel)}>
            <FollowIcon iconStyle={iconStyle} />
            <div className={styles.title}>
                <h2
                    className={styles.titleText}
                >
                    <StepBadge number={stepNumber} />お名前とご年齢は？
                </h2>
            </div>

            <div className={styles.formContainer}>
                <div className={styles.inputRow} ref={nameFieldRef}>
                    <div className={styles.inputField}>
                        <div className={styles.labelWrapper}>
                            <label className={styles.label}>姓</label>
                            <span className={styles.required}>必須</span>
                        </div>
                        <input
                            type='text'
                            autoComplete='family-name'
                            className={`${styles.textInput} ${showLastNameError ? styles.inputError : ''}`}
                            value={formData.lastName}
                            onChange={handleLastNameChange}
                            onBlur={() => handleBlur('lastName')}
                            placeholder='山田'
                        />
                        {showLastNameError && (
                            <div className={styles.errorMessage}>{errors.lastName}</div>
                        )}
                    </div>

                    <div className={styles.inputField}>
                        <div className={styles.labelWrapper}>
                            <label className={styles.label}>名</label>
                            <span className={styles.required}>必須</span>
                        </div>
                        <input
                            type='text'
                            autoComplete='given-name'
                            className={`${styles.textInput} ${showFirstNameError ? styles.inputError : ''}`}
                            value={formData.firstName}
                            onChange={handleFirstNameChange}
                            onBlur={() => handleBlur('firstName')}
                            placeholder='太郎'
                        />
                        {showFirstNameError && (
                            <div className={styles.errorMessage}>{errors.firstName}</div>
                        )}
                    </div>
                </div>

                <div className={styles.inputRow} ref={ageFieldRef}>
                    <div className={styles.inputField}>
                        <div className={styles.labelWrapper}>
                            <label className={styles.label}>年齢</label>
                            <span className={styles.required}>必須</span>
                        </div>
                        <input
                            type='number'
                            className={`${styles.textInput} ${showAgeError ? styles.inputError : ''}`}
                            value={formData.age}
                            onChange={handleAgeChange}
                            onFocus={() => setIsAgeFocused(true)}
                            onBlur={() => handleBlur('age')}
                            placeholder='20'
                            min='1'
                            max='99'
                        />
                        {showAgeError && (
                            <div className={styles.errorMessage}>{errors.age}</div>
                        )}
                    </div>
                </div>
            </div>

            <NextButton
                ref={nextButtonRef}
                disabled={!isFormValid}
                dataClickLabel={dataClickLabel(gtmTags.dataClickLabels.next)}
                onClick={() => {
                    // DEV-3061: フロンティアstepのみ遷移
                    if (currentStep === stepNumber - 1) {
                        handleNext()
                    }
                }}
            />
        </div>
    )
}

export default FullNameVertical
